import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { DetailItem, WebsiteContent } from '../types/content';
import { appConfig } from '../config/appConfig';
import {
  isContentCacheFresh,
  readContentCacheEntry,
  readFreshContentCache,
} from '../lib/contentCache';
import { loadWebsiteContent } from '../lib/contentLoader';
import { prefetchCategoryImages, prefetchSiteAssets } from '../lib/prefetchAssets';

interface ContentContextValue {
  data: WebsiteContent | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  getItem: (id: string) => DetailItem | undefined;
}

const ContentContext = createContext<ContentContextValue | null>(null);

function getInitialContent(): WebsiteContent | null {
  return readFreshContentCache(appConfig.contentRevalidateMs);
}

export function ContentProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<WebsiteContent | null>(getInitialContent);
  const [loading, setLoading] = useState(() => getInitialContent() === null);
  const [error, setError] = useState<string | null>(null);
  const loadGeneration = useRef(0);

  const load = useCallback(async (force = false) => {
    const generation = ++loadGeneration.current;
    const cached = readContentCacheEntry();

    if (!force && cached) {
      setData(cached.data);
      setError(null);
      setLoading(false);

      if (isContentCacheFresh(cached, appConfig.contentRevalidateMs)) {
        return;
      }
    } else {
      setLoading(true);
      setError(null);
    }

    try {
      const content = await loadWebsiteContent({ force });
      if (generation !== loadGeneration.current) return;
      setData(content);
      setError(null);
      prefetchCategoryImages(content.highLevelIssues);
    } catch (e) {
      if (generation !== loadGeneration.current) return;
      const message = e instanceof Error ? e.message : 'Unknown error loading content';
      const stale = readContentCacheEntry()?.data;
      if (stale) {
        setData(stale);
        setError(null);
      } else {
        setError(message);
        setData(null);
      }
    } finally {
      if (generation === loadGeneration.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    prefetchSiteAssets();
    void load(false);
  }, [load]);

  useEffect(() => {
    if (data?.highLevelIssues?.length) {
      prefetchCategoryImages(data.highLevelIssues);
    }
  }, [data]);

  const getItem = useCallback(
    (id: string) => (data ? data.index.byId[id] : undefined),
    [data],
  );

  const value = useMemo(
    () => ({ data, loading, error, reload: () => void load(true), getItem }),
    [data, loading, error, load, getItem],
  );

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useContent() {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error('useContent must be used within ContentProvider');
  return ctx;
}
